import { useState } from 'react';
import { Plus, ChevronDown, Building2 } from 'lucide-react';
import { useGameStore } from '@/state/gameStore';
import { STATION_SPECS } from '@/data/stations';
import type { Station, Vehicle, VehicleStatus } from '@/state/types';
import { VEHICLE_SPECS } from '@/data/vehicles';

interface StationsTabProps {
  onRequestBuildStation: () => void;
}

const STATUS_COLOR: Record<VehicleStatus, string> = {
  1: 'bg-status-available text-bg-primary',
  2: 'bg-status-available text-bg-primary',
  3: 'bg-status-enroute text-white',
  4: 'bg-status-onscene text-white',
  5: 'bg-accent-dispatch text-white',
  6: 'bg-status-offduty text-white',
  7: 'bg-status-transport text-white',
  8: 'bg-status-transport text-white',
};

export function StationsTab({ onRequestBuildStation }: StationsTabProps) {
  const stations = useGameStore((s) => s.stations);
  const vehicles = useGameStore((s) => s.vehicles);
  const selectDetail = useGameStore((s) => s.selectDetail);
  const pushToast = useGameStore((s) => s.pushToast);

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between border-b border-border-subtle px-5 py-3">
        <h2 className="font-display text-base font-bold">Wachen ({stations.length})</h2>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onRequestBuildStation}
            className="inline-flex items-center gap-1.5 rounded-full bg-accent-emergency px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-accent-emergency-hover"
          >
            <Plus className="h-3 w-3" aria-hidden />
            Wache bauen
          </button>
          <button
            type="button"
            aria-label="Filter"
            onClick={() =>
              pushToast({ kind: 'info', text: 'Filter-Funktion kommt in der nächsten Phase.' })
            }
            className="flex h-8 w-8 items-center justify-center rounded-md text-fg-muted transition hover:bg-bg-elevated hover:text-fg-primary"
          >
            <span className="sr-only">Filter</span>
            ⋯
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        {stations.length === 0 ? (
          <BigEmptyState onBuild={onRequestBuildStation} />
        ) : (
          <ul className="space-y-3">
            {stations.map((s) => (
              <StationGroup
                key={s.id}
                station={s}
                vehicles={vehicles.filter((v) => v.stationId === s.id)}
                onSelectStation={() => selectDetail({ kind: 'station', id: s.id })}
                onSelectVehicle={(v) => selectDetail({ kind: 'vehicle', id: v.id })}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

interface StationGroupProps {
  station: Station;
  vehicles: Vehicle[];
  onSelectStation: () => void;
  onSelectVehicle: (v: Vehicle) => void;
}

function StationGroup({ station, vehicles, onSelectStation, onSelectVehicle }: StationGroupProps) {
  const [expanded, setExpanded] = useState(true);
  const spec = STATION_SPECS[station.kind];

  return (
    <li className="overflow-hidden rounded-lg border border-border-subtle bg-bg-elevated/30">
      <div className="flex items-stretch">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex flex-1 items-center gap-3 px-4 py-3 text-left transition hover:bg-bg-elevated/60"
          aria-expanded={expanded}
        >
          <span
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md"
            style={{ background: spec.markerColor }}
          >
            <Building2 className="h-4 w-4 text-white" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate font-display text-sm font-bold">{station.name}</p>
            <p className="text-xs text-fg-muted">
              {spec.shortLabel} · {vehicles.length} Fahrzeug{vehicles.length === 1 ? '' : 'e'}
            </p>
          </div>
          <ChevronDown
            className={[
              'h-4 w-4 flex-shrink-0 text-fg-muted transition-transform',
              expanded ? 'rotate-180' : '',
            ].join(' ')}
            aria-hidden
          />
        </button>
        <button
          type="button"
          onClick={onSelectStation}
          className="border-l border-border-subtle px-3 text-xs font-semibold text-accent-dispatch transition hover:bg-bg-elevated"
        >
          Details
        </button>
      </div>

      {expanded && vehicles.length > 0 && (
        <ul className="grid grid-cols-1 gap-1.5 border-t border-border-subtle p-3 md:grid-cols-2">
          {vehicles.map((v) => {
            const vSpec = VEHICLE_SPECS[v.type];
            return (
              <li key={v.id}>
                <button
                  type="button"
                  onClick={() => onSelectVehicle(v)}
                  className="flex w-full items-center gap-2.5 rounded-md border border-transparent bg-bg-elevated/50 px-2.5 py-2 text-left transition hover:border-border-subtle hover:bg-bg-elevated"
                >
                  <span
                    className={[
                      'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md font-display text-base font-bold',
                      STATUS_COLOR[v.status],
                    ].join(' ')}
                  >
                    {v.status}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold">{vSpec.label}</p>
                    <p className="truncate text-[10px] text-fg-muted">{v.id.slice(-6)}</p>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {expanded && vehicles.length === 0 && (
        <p className="border-t border-border-subtle px-4 py-3 text-center text-xs text-fg-muted">
          Keine Fahrzeuge stationiert.
        </p>
      )}
    </li>
  );
}

function BigEmptyState({ onBuild }: { onBuild: () => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 py-12 text-center">
      <span className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-dashed border-border-strong text-fg-muted">
        <Building2 className="h-7 w-7" aria-hidden />
      </span>
      <p className="font-display text-lg font-semibold">Noch keine Wachen</p>
      <p className="mt-1.5 max-w-xs text-xs text-fg-muted">
        Baue deine erste Wache, um Fahrzeuge zu stationieren und Einsätze abzuarbeiten.
      </p>
      <button
        type="button"
        onClick={onBuild}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-accent-emergency px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-accent-emergency-hover"
      >
        <Plus className="h-4 w-4" aria-hidden />
        Erste Wache bauen
      </button>
    </div>
  );
}

